import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { KeyRound, ArrowRight, Coffee, ArrowLeft } from "lucide-react";
import { adminLogin } from "../../lib/admin";
import { startGoogleLogin } from "../../lib/auth";
import { toast } from "sonner";

const AdminLogin = () => {
    const nav = useNavigate();
    const { slug } = useParams();
    const [pin, setPin] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const submit = async () => {
        if (pin.length < 4) return;
        setSubmitting(true);
        try {
            const r = await adminLogin(slug, pin);
            nav(`/${slug}/admin/dashboard`, { replace: true, state: { name: r?.tenant_name } });
        } catch {
            toast.error("Wrong PIN");
            setPin("");
            setSubmitting(false);
        }
    };

    const googleSignIn = () => startGoogleLogin(`/${slug}/admin/dashboard`);

    return (
        <div className="min-h-screen bg-[#F9F6F0] flex items-center justify-center p-6">
            <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                className="w-full max-w-sm text-center"
            >
                <button
                    data-testid="admin-back-btn"
                    onClick={() => nav(`/${slug}`)}
                    className="mb-6 flex items-center gap-2 text-sm font-semibold text-[#8C7A72] hover:text-[#2C1A12] mx-auto"
                >
                    <ArrowLeft className="h-4 w-4" strokeWidth={2.5} /> Back to café
                </button>

                <div className="inline-flex h-14 w-14 rounded-full bg-[#FF4F18] items-center justify-center text-white">
                    <KeyRound className="h-6 w-6" strokeWidth={2.5} />
                </div>
                <h1 className="mt-6 font-display font-black text-4xl">
                    Coffee<span className="text-[#FF4F18]">in7.</span>
                </h1>
                <div className="mt-1 text-xs uppercase tracking-[0.25em] text-[#8C7A72] font-semibold">
                    /{slug} · Owner admin
                </div>

                <button
                    data-testid="admin-google-btn"
                    onClick={googleSignIn}
                    className="mt-10 w-full py-4 rounded-full bg-[#2C1A12] text-white font-display font-bold text-base flex items-center justify-center gap-2 active:scale-[0.98] shadow-[0_12px_32px_rgba(44,26,18,0.32)]"
                >
                    <GoogleIcon />
                    Sign in with Google
                </button>

                <div className="mt-6 flex items-center gap-3">
                    <div className="flex-1 h-px bg-[#E8DCCB]" />
                    <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#8C7A72]">
                        or use PIN
                    </div>
                    <div className="flex-1 h-px bg-[#E8DCCB]" />
                </div>

                <input
                    data-testid="admin-pin-input"
                    type="password"
                    inputMode="numeric"
                    maxLength={12}
                    placeholder="Enter PIN"
                    value={pin}
                    onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
                    onKeyDown={(e) => e.key === "Enter" && submit()}
                    className="mt-4 w-full text-center text-3xl font-display font-black tracking-[0.4em] py-4 px-5 rounded-2xl bg-white border-2 border-[#E8DCCB] focus:border-[#FF4F18] outline-none transition-colors"
                />

                <button
                    data-testid="admin-pin-submit"
                    disabled={pin.length < 4 || submitting}
                    onClick={submit}
                    className={`mt-4 w-full py-3.5 rounded-full font-display font-bold inline-flex items-center justify-center gap-2 ${
                        pin.length >= 4 && !submitting
                            ? "bg-[#FF4F18] text-white active:scale-[0.98]"
                            : "bg-[#E8DCCB] text-[#8C7A72]"
                    }`}
                >
                    {submitting ? "Signing in…" : "Unlock with PIN"}
                    {!submitting && <ArrowRight className="h-4 w-4" strokeWidth={2.5} />}
                </button>

                <div className="mt-10 flex items-center justify-center gap-2 text-[#8C7A72] text-xs">
                    <Coffee className="h-3.5 w-3.5" strokeWidth={2.5} />
                    <span>Owner: Google · Staff: PIN — both give same access</span>
                </div>
            </motion.div>
        </div>
    );
};

const GoogleIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <path fill="#FFF" d="M12 10.2v3.9h5.5c-.2 1.4-1.6 4.1-5.5 4.1-3.3 0-6-2.7-6-6.1s2.7-6.1 6-6.1c1.9 0 3.1.8 3.8 1.5l2.6-2.5C16.7 3.4 14.6 2.4 12 2.4 6.8 2.4 2.6 6.6 2.6 12S6.8 21.6 12 21.6c6.9 0 9.5-4.8 9.5-7.3 0-.5 0-.9-.1-1.3L12 10.2z"/>
    </svg>
);

export default AdminLogin;
